/**
 * P12 项目域 Copilot 交互 — 从 legacy-app.js 拆出。
 */
import { navigatePortalPage } from '../nav.js';
import { escapeHtml } from '../util/html.js';

/** @param {import('./portal-ui-deps.js').PortalUiDeps} deps */
export function initCopilotUi(deps) {
  const {
    requestRuntime,
    showRuntimeError,
    setRuntimeProgress,
    getCurrentProjectId,
  } = deps;

  let latestCopilotContext = null;
  let copilotContextLoadedFor = '';
  let copilotAskInFlight = false;

  function ensureCopilotContextLoaded(force) {
    const currentProjectId = getCurrentProjectId();
    if (!force && copilotContextLoadedFor && copilotContextLoadedFor === currentProjectId) {
      return;
    }
    void loadCopilotContext();
  }

  async function loadCopilotContext() {
    const currentProjectId = getCurrentProjectId();
    const meta = document.getElementById('copilot-context-meta');
    const badge = document.getElementById('copilot-online-badge');
    const header = document.getElementById('copilot-header-ctx');
    const snapshot = document.getElementById('copilot-snapshot');
    if (!currentProjectId) {
      latestCopilotContext = null;
      copilotContextLoadedFor = '';
      if (badge) {
        badge.textContent = '未选项目';
        badge.style.color = '#F59E0B';
      }
      if (header) header.textContent = '等待项目上下文';
      if (meta) meta.textContent = '请先在立项页选择或创建项目，再刷新本页上下文。';
      if (snapshot) snapshot.textContent = '尚未选择项目';
      document.getElementById('copilot-quick-actions').innerHTML = '';
      document.getElementById('stage-result-p12').textContent = '未选择项目：无法加载 Copilot 上下文。';
      return;
    }
    try {
      const result = await requestRuntime(
        `/api/business/copilot/context?projectId=${encodeURIComponent(currentProjectId)}`
      );
      latestCopilotContext = result;
      copilotContextLoadedFor = currentProjectId;
      if (badge) {
        badge.textContent = '已连接';
        badge.style.color = '#16A34A';
      }
      if (header) {
        header.textContent = `已感知 · ${result.projectName || currentProjectId}`;
      }
      if (meta) {
        meta.textContent = result.greeting || `已加载项目 ${result.projectName || currentProjectId}`;
      }
      renderCopilotSnapshot(result);
      renderCopilotQuickActions(result.suggestedActions || []);
      const log = document.getElementById('copilot-chat-log');
      if (log && (log.querySelector('[data-copilot-empty]') || !log.querySelector('[data-copilot-msg]'))) {
        log.innerHTML = '';
        appendCopilotBubble('assistant', `
        <div style="white-space:pre-wrap;">${escapeHtml(result.greeting || '')}</div>
        <div style="margin-top:8px;font-size:10px;color:#9CA3AF;">依据：ProjectContext · 不编造外部事实</div>
      `);
      }
      document.getElementById('stage-result-p12').textContent =
        `上下文已加载 · ${result.projectName || currentProjectId} · 阶段 ${result.stage || '--'} · 风险 ${result.riskCount || 0} · 交付物 ${result.artifactCount || 0}`;
    } catch (error) {
      copilotContextLoadedFor = '';
      if (badge) {
        badge.textContent = '离线/失败';
        badge.style.color = '#EF4444';
      }
      if (header) header.textContent = '上下文加载失败';
      if (meta) meta.textContent = `加载失败：${error.message}`;
      if (snapshot) snapshot.textContent = `加载失败：${error.message}`;
      document.getElementById('stage-result-p12').textContent = `Copilot 上下文失败：${error.message}`;
      showRuntimeError(error.message);
    }
  }

  function renderCopilotSnapshot(result) {
    const snapshot = document.getElementById('copilot-snapshot');
    if (!snapshot) return;
    snapshot.innerHTML = [
      `<div><span style="color:#9CA3AF;">项目</span> ${escapeHtml(result.projectName || '--')}</div>`,
      `<div><span style="color:#9CA3AF;">公司</span> ${escapeHtml(result.companyName || '--')}</div>`,
      `<div><span style="color:#9CA3AF;">行业</span> ${escapeHtml(result.industryName || '--')}</div>`,
      `<div><span style="color:#9CA3AF;">阶段</span> ${escapeHtml(result.stage || '--')}</div>`,
      `<div><span style="color:#9CA3AF;">决策</span> ${escapeHtml(result.decisionResult || '--')}</div>`,
      `<div><span style="color:#9CA3AF;">评估分</span> ${result.evaluationScore != null ? escapeHtml(String(result.evaluationScore)) : '--'}</div>`,
      `<div><span style="color:#9CA3AF;">风险</span> ${result.riskCount || 0}（高 ${result.highRiskCount || 0}）</div>`,
      `<div><span style="color:#9CA3AF;">交付物</span> ${result.artifactCount || 0}</div>`,
    ].join('');
  }

  function renderCopilotQuickActions(actions) {
    const box = document.getElementById('copilot-quick-actions');
    if (!box) return;
    // 规范化并补齐页面编排芯片（服务端若未返回 page 时的 Portal 侧兜底）
    const list = normalizeCopilotSuggestedActions(actions, latestCopilotContext);
    box.innerHTML = list.map((a) => {
      const label = escapeHtml(a.label || a.intent || '动作');
      const page = a.page ? String(a.page) : '';
      const intent = a.intent ? String(a.intent) : '';
      if (page) {
        return `<button type="button" data-copilot-page="${escapeHtml(page)}" data-copilot-label="${label}"
        title="跳转到 ${escapeHtml(page)}"
        style="background:#EEF2FF;padding:5px 10px;border-radius:12px;font-size:10px;cursor:pointer;border:1px solid #C7D2FE;color:#3730A3;">${label}</button>`;
      }
      return `<button type="button" data-copilot-intent="${escapeHtml(intent || 'ask')}" data-copilot-label="${label}"
      style="background:#F3F4F6;padding:5px 10px;border-radius:12px;font-size:10px;cursor:pointer;border:1px solid #E5E7EB;">${label}</button>`;
    }).join('');
  }

  /** 将 ask/context 的 suggestedActions 规范为 {label,page,intent}，并补齐常用 Portal 跳转。 */
  function normalizeCopilotSuggestedActions(actions, context) {
    const raw = Array.isArray(actions) ? actions : [];
    const list = raw.map((a) => {
      if (typeof a === 'string') return { label: a, intent: 'ask' };
      return {
        label: a.label || a.intent || '动作',
        intent: a.intent || undefined,
        page: a.page || undefined
      };
    });
    const hasPage = list.some((a) => a.page);
    if (!hasPage) {
      // 服务端冲突/旧版时：按上下文意图补齐编排跳转（不触发 Workflow）
      const stage = String(context?.stage || '').toLowerCase();
      const riskCount = Number(context?.riskCount || 0);
      const fallback = [
        { label: '去智能立项', page: 'p1' },
        { label: '进入尽调问题助手', page: 'p3' },
        { label: '进入投后风控', page: 'p8' },
        { label: '打开风险预警', page: 'p10' },
        { label: '去知识库问答', page: 'p11' }
      ];
      if (/投委|committee|ic|投决/.test(stage)) {
        fallback.splice(2, 0, { label: '打开投决管理', page: 'p6' });
      }
      if (riskCount <= 0 && !/投后|post|monitor/.test(stage)) {
        // 无风险时仍保留预警入口，去掉重复投后可保留
      }
      for (const item of fallback) {
        if (!list.some((a) => a.page === item.page)) list.push(item);
      }
    }
    return list.slice(0, 10);
  }

  /** 点击建议动作：有 page 则导航 Portal，否则继续追问；绝不自动跑 Workflow。 */

  function renderCopilotFollowups(questions) {
    const box = document.getElementById('copilot-followups');
    if (!box) return;
    const list = Array.isArray(questions) ? questions : [];
    box.innerHTML = list.slice(0, 4).map((q) =>
      `<button type="button" data-copilot-followup="${escapeHtml(q)}" style="background:#FFF7ED;padding:4px 10px;border-radius:12px;font-size:10px;cursor:pointer;border:1px solid #F3E8D0;color:#92400E;">${escapeHtml(q)}</button>`
    ).join('');
  }

  function renderCopilotCitations(citations) {
    const box = document.getElementById('copilot-citations');
    if (!box) return;
    const list = Array.isArray(citations) ? citations : [];
    if (!list.length) {
      box.textContent = '本次无结构化引用';
      return;
    }
    box.innerHTML = list.slice(0, 10).map((c) =>
      `<div style="margin-bottom:8px;padding-bottom:6px;border-bottom:1px solid #F3F4F6;">
      <div style="color:#0B1426;font-weight:600;">${escapeHtml(c.title || c.id || '')}</div>
      <div style="color:#9CA3AF;">${escapeHtml(c.kind || '')}</div>
      <div>${escapeHtml(c.excerpt || '')}</div>
    </div>`
    ).join('');
  }

  function appendCopilotBubble(role, html) {
    const log = document.getElementById('copilot-chat-log');
    if (!log) return;
    const emptyHint = log.querySelector('[data-copilot-empty]');
    if (emptyHint) emptyHint.remove();
    const wrap = document.createElement('div');
    wrap.setAttribute('data-copilot-msg', '1');
    wrap.style.cssText = role === 'user'
      ? 'display:flex;justify-content:flex-end;margin-bottom:12px;'
      : 'display:flex;justify-content:flex-start;margin-bottom:12px;';
    wrap.innerHTML = role === 'user'
      ? `<div style="background:#0B1426;color:#fff;padding:10px 14px;border-radius:12px 12px 2px 12px;max-width:85%;font-size:12px;line-height:1.6;">${html}</div>`
      : `<div style="background:#F5F1EB;border:1px solid #E5E7EB;padding:12px 14px;border-radius:12px 12px 12px 2px;max-width:92%;font-size:12px;line-height:1.6;">${html}</div>`;
    log.appendChild(wrap);
    log.scrollTop = log.scrollHeight;
  }

  async function askCopilot(message, intent) {
    const q = String(message || '').trim();
    if (!q) {
      showRuntimeError('请输入要询问的内容。');
      return;
    }
    const currentProjectId = getCurrentProjectId();
    if (!currentProjectId) {
      showRuntimeError('请先选择或立项一个项目。');
      return;
    }
    if (copilotAskInFlight) return;
    copilotAskInFlight = true;
    const askBtn = document.getElementById('copilot-ask-btn');
    const originalText = askBtn?.textContent || '发送';
    if (askBtn) {
      askBtn.disabled = true;
      askBtn.textContent = '分析中...';
    }
    appendCopilotBubble('user', escapeHtml(q));
    setRuntimeProgress(35);
    try {
      const result = await requestRuntime('/api/business/copilot/ask', {
        method: 'POST',
        body: JSON.stringify({
          projectId: currentProjectId,
          message: q,
          intent: intent || undefined,
          topK: 5
        })
      });
      if (result.contextSummary) {
        latestCopilotContext = result.contextSummary;
        renderCopilotSnapshot(result.contextSummary);
        renderCopilotQuickActions(result.contextSummary.suggestedActions || []);
      }
      const citations = Array.isArray(result.citations) ? result.citations : [];
      renderCopilotCitations(citations);
      renderCopilotFollowups(result.followUps);
      const modeLabel =
        result.answerMode === 'no_match' ? '无命中'
          : result.answerMode === 'no_context' ? '无上下文'
            : '有据回答';
      const warnHtml = Array.isArray(result.warnings) && result.warnings.length
        ? `<div style="margin-top:6px;font-size:10px;color:#B45309;">${escapeHtml(result.warnings.join('；'))}</div>`
        : '';
      appendCopilotBubble('assistant', `
      <div style="font-size:10px;color:#9CA3AF;margin-bottom:6px;">${escapeHtml(modeLabel)} · ${escapeHtml(result.intent || '')} · ${escapeHtml(result.taskId || '')}</div>
      <div style="white-space:pre-wrap;">${escapeHtml(result.answer || '')}</div>
      ${warnHtml}
      <div style="margin-top:8px;font-size:10px;color:#9CA3AF;">${escapeHtml(result.disclaimer || '')}</div>
    `);
      document.getElementById('stage-result-p12').innerHTML =
        `<strong>Copilot 完成</strong> · ${escapeHtml(modeLabel)} · 引用 ${citations.length} 条 · <code style="font-size:10px;">${escapeHtml(result.taskId || '')}</code>`;
      setRuntimeProgress(100);
    } catch (error) {
      appendCopilotBubble('assistant', `提问失败：${escapeHtml(error.message)}`);
      document.getElementById('stage-result-p12').textContent = `Copilot 失败：${error.message}`;
      showRuntimeError(error.message);
      setRuntimeProgress(0);
    } finally {
      copilotAskInFlight = false;
      if (askBtn) {
        askBtn.disabled = false;
        askBtn.textContent = originalText;
      }
    }
  }

  document.getElementById('copilot-refresh-btn')?.addEventListener('click', function() {
    void loadCopilotContext();
  });

  document.getElementById('copilot-ask-btn')?.addEventListener('click', function() {
    const q = document.getElementById('copilot-input')?.value || '';
    void askCopilot(q);
  });

  document.getElementById('copilot-input')?.addEventListener('keydown', function(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void askCopilot(this.value);
    }
  });

  document.getElementById('copilot-quick-actions')?.addEventListener('click', function(event) {
    const pageBtn = event.target.closest('[data-copilot-page]');
    if (pageBtn) {
      const page = pageBtn.getAttribute('data-copilot-page') || '';
      // 仅跳转 Portal 对应页，不自动触发 Workflow
      if (navigatePortalPage(page)) return;
      showRuntimeError(`未找到页面：${page}`);
      return;
    }
    const button = event.target.closest('[data-copilot-intent]');
    if (!button) return;
    const intent = button.getAttribute('data-copilot-intent') || 'ask';
    const label = button.getAttribute('data-copilot-label') || intent;
    const input = document.getElementById('copilot-input');
    if (input) input.value = label;
    void askCopilot(label, intent);
  });

  document.getElementById('copilot-followups')?.addEventListener('click', function(event) {
    const button = event.target.closest('[data-copilot-followup]');
    if (!button) return;
    const q = button.getAttribute('data-copilot-followup') || '';
    if (!q) return;
    const input = document.getElementById('copilot-input');
    if (input) input.value = q;
    void askCopilot(q);
  });

  return { ensureLoaded: ensureCopilotContextLoaded };
}
